'use client';

import { ArrowLeft, ArrowRight, CheckCircle2, Minus, Plus } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useMemo, useState } from 'react';

import { useProductInteraction } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-product-interaction';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import type { CartSubselection } from '@/app/(users)/totem-by-slug/[slug]/types/cart-item';
import type { MenuSnapshotProduct } from '@/app/(users)/totem-by-slug/[slug]/types/menu-snapshot';
import { getProductDescription, getProductDisplayName } from '@/app/(users)/totem-by-slug/[slug]/utils/product-name';
import { formatTotemPrice } from '@/app/(users)/totem-by-slug/[slug]/utils/totem-price';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Database } from '@/types/supabase';

type SubselectionItem = Database['public']['Tables']['subselection_item']['Row'] & {
  product_stock?: Database['public']['Tables']['product_stock']['Row'] | null;
};

function SubselectionGroup({
  groupIndex,
  name,
  minSelection,
  maxSelection,
  single,
  items,
  selected,
  onChange,
}: {
  groupIndex: number;
  name: string;
  minSelection: number;
  maxSelection: number;
  single: boolean;
  items: SubselectionItem[];
  selected: Record<string, number>;
  onChange: (itemId: string, quantity: number) => void;
}) {
  const totalSelected = Object.values(selected).reduce((sum, qty) => sum + qty, 0);

  const groupDescription = minSelection > 0 ? `Elegí al menos ${minSelection} opciones` : 'Opcional';

  return (
    <div className="flex flex-col gap-4 mt-6 first:mt-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-white font-bold">
          {groupIndex}
        </div>
        <div className="flex flex-col">
          <h3 className="text-lg font-bold">{name}</h3>
          <span className="text-sm text-muted-foreground">{groupDescription}</span>
        </div>
      </div>

      {/* Content */}
      {single ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {items.map(item => {
            const isSelected = (selected[item.id] ?? 0) > 0;
            const isOutOfStock = item.product_stock?.stock === 0;
            const isMaxReached = maxSelection > 0 && totalSelected >= maxSelection;
            // Prevent selection if out of stock, OR if it's not selected and we've hit the limit (unless maxSelection === 1, where clicking just swaps it)
            const isDisabled = isOutOfStock || (!isSelected && isMaxReached && maxSelection !== 1);

            return (
              <button
                key={item.id}
                type="button"
                disabled={isDisabled}
                className={cn(
                  'relative flex flex-col items-center gap-3 rounded-xl border-2 p-3 transition-all outline-none',
                  isDisabled && !isSelected
                    ? 'opacity-50 cursor-not-allowed bg-muted/20 border-transparent grayscale'
                    : '',
                  isOutOfStock
                    ? 'opacity-50 cursor-not-allowed grayscale bg-muted/20 border-transparent'
                    : isSelected
                      ? 'border-emerald-500 bg-emerald-500/5'
                      : 'border-transparent bg-muted/30 hover:bg-muted/50'
                )}
                onClick={() => {
                  if (isSelected) {
                    onChange(item.id, 0);
                  } else {
                    if (maxSelection === 1) {
                      // Deselect others
                      items.forEach(i => {
                        if (i.id !== item.id && selected[i.id]) {
                          onChange(i.id, 0);
                        }
                      });
                    } else if (isMaxReached) {
                      return; // Prevent selection if max is reached
                    }
                    onChange(item.id, 1);
                  }
                }}
              >
                {/* Image Container */}
                <div className="relative aspect-square w-full rounded-lg overflow-hidden bg-white/50">
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-10 rounded-full bg-white text-emerald-500 shadow-sm">
                      <CheckCircle2 className="size-6 fill-emerald-500 text-white" />
                    </div>
                  )}
                  {item.price > 0 && (
                    <div className="absolute top-2 right-2 z-10 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
                      +${item.price}
                    </div>
                  )}
                  {item.image_url ? (
                    <Image src={item.image_url} alt={item.name} fill className="object-cover" sizes="150px" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-100 p-2 text-center text-sm font-medium text-muted-foreground">
                      {item.name}
                    </div>
                  )}
                </div>

                {/* Name */}
                <span
                  className={cn(
                    'text-sm font-medium text-center line-clamp-2 leading-tight min-h-[2.5rem]',
                    isOutOfStock && 'text-muted-foreground line-through decoration-muted-foreground'
                  )}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-2xl bg-muted/30 p-2">
          {items.map((item, index) => {
            const qty = selected[item.id] ?? 0;
            const isLast = index === items.length - 1;
            const isOutOfStock = item.product_stock?.stock === 0;
            const isMaxReached = maxSelection > 0 && totalSelected >= maxSelection;

            return (
              <div
                key={item.id}
                className={cn('flex flex-col gap-2 p-3', !isLast && 'border-b', isOutOfStock && 'opacity-50 grayscale')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col flex-1">
                    <span className={cn('font-medium text-base', isOutOfStock && 'line-through text-muted-foreground')}>
                      {item.name}
                    </span>
                    {item.price > 0 && (
                      <span className="text-sm text-muted-foreground">+{formatTotemPrice(item.price)}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      disabled={qty <= 0 || isOutOfStock}
                      onClick={() => onChange(item.id, Math.max(0, qty - 1))}
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white disabled:opacity-50 disabled:bg-gray-300 transition-colors"
                    >
                      <Minus className="size-4" />
                    </button>

                    <span className="w-4 text-center font-semibold text-lg">{qty}</span>

                    <button
                      type="button"
                      disabled={
                        isOutOfStock || (isMaxReached && maxSelection !== 1) || (maxSelection === 1 && qty >= 1)
                      }
                      onClick={() => {
                        if (maxSelection === 1 && qty === 0) {
                          items.forEach(i => {
                            if (i.id !== item.id && (selected[i.id] ?? 0) > 0) {
                              onChange(i.id, 0);
                            }
                          });
                          onChange(item.id, 1);
                        } else {
                          onChange(item.id, qty + 1);
                        }
                      }}
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white disabled:opacity-50 disabled:bg-gray-300 transition-colors"
                    >
                      <Plus className="size-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function buildSubselectionPayload(
  product: MenuSnapshotProduct,
  selections: Record<string, Record<string, number>>
): CartSubselection[] {
  const result: CartSubselection[] = [];

  for (const ps of product.product_subselection ?? []) {
    const groupSelections = selections[ps.subselection_id] ?? {};
    const items = ps.subselection?.subselection_item ?? [];

    for (const [itemId, quantity] of Object.entries(groupSelections)) {
      if (quantity <= 0) continue;
      const item = items.find(i => i.id === itemId);
      if (!item) continue;
      result.push({
        subselectionItemId: item.id,
        name: item.name,
        price: item.price,
        quantity,
      });
    }
  }

  return result;
}

function isSubselectionValid(
  product: MenuSnapshotProduct,
  selections: Record<string, Record<string, number>>
): boolean {
  for (const ps of product.product_subselection ?? []) {
    const sub = ps.subselection;
    if (!sub) continue;

    const groupSelections = selections[ps.subselection_id] ?? {};
    const total = Object.values(groupSelections).reduce((sum, qty) => sum + qty, 0);

    if (total < sub.min_selection) return false;
    if (sub.max_selection > 0 && total > sub.max_selection) return false;
  }
  return true;
}

export function TotemProductSheet() {
  const { selectedProduct, setSelectedProduct, cart, editingCartItemId, setEditingCartItemId } = useTotem();
  const { addProductToCart } = useProductInteraction();
  const [selections, setSelections] = useState<Record<string, Record<string, number>>>({});

  const product = selectedProduct;
  const open = Boolean(product);

  const priceFrom = useMemo(() => {
    if (!product || Number(product.price) > 0) return 0;
    let min = 0;
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const isOutOfStock = item.product_stock?.stock === 0;
        if (!isOutOfStock && item.price > 0 && (min === 0 || item.price < min)) {
          min = item.price;
        }
      }
    }
    return min;
  }, [product]);

  const resetState = useCallback(() => {
    setSelections({});
    setEditingCartItemId(null);
  }, [setEditingCartItemId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedProduct(null);
      resetState();
    }
  };

  const handleSubselectionChange = (subselectionId: string, itemId: string, quantity: number) => {
    setSelections(prev => ({
      ...prev,
      [subselectionId]: {
        ...(prev[subselectionId] ?? {}),
        [itemId]: quantity,
      },
    }));
  };

  // Pre-fill state when editing an item
  useMemo(() => {
    if (product && editingCartItemId) {
      const editingItem = cart.items.find(i => i.id === editingCartItemId);
      if (editingItem) {
        const newSelections: Record<string, Record<string, number>> = {};
        for (const ps of product.product_subselection ?? []) {
          const sub = ps.subselection;
          if (!sub) continue;
          for (const item of sub.subselection_item ?? []) {
            const cartSub = editingItem.subselections.find(s => s.subselectionItemId === item.id);
            if (cartSub) {
              if (!newSelections[ps.subselection_id]) newSelections[ps.subselection_id] = {};
              newSelections[ps.subselection_id][item.id] = cartSub.quantity;
            }
          }
        }
        setSelections(newSelections);
      }
    } else if (product && !editingCartItemId) {
      // Just opened for a new product, state was already reset
    }
  }, [product, editingCartItemId, cart.items]);

  const subselections = useMemo(
    () => (product ? buildSubselectionPayload(product, selections) : []),
    [product, selections]
  );

  const isValid = product ? isSubselectionValid(product, selections) : false;

  const handleAdd = () => {
    if (!product || !isValid) return;

    if (editingCartItemId) {
      const editingItem = cart.items.find(i => i.id === editingCartItemId);
      const quantity = editingItem?.quantity ?? 1;

      cart.updateItem(editingCartItemId, {
        productId: product.id,
        name: getProductDisplayName(product),
        imageUrl: product.imageurl,
        unitPrice: Number(product.price),
        quantity,
        subselections,
      });
    } else {
      addProductToCart(product, 1, subselections);
    }

    setSelectedProduct(null);
    resetState();
  };

  if (!product) return null;

  const name = getProductDisplayName(product);

  let groupCounter = 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[95vh] overflow-hidden flex flex-col w-[95vw] sm:max-w-xl md:max-w-2xl p-0 gap-0 border-0 bg-background/95 backdrop-blur-md rounded-2xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{name}</DialogTitle>
        <DialogDescription className="sr-only">Personaliza tu {name}</DialogDescription>

        <div className="flex flex-col h-full min-h-[50vh] max-h-[90vh]">
          {/* Custom Header */}
          <div className="flex shrink-0 items-center gap-6 px-6 py-4 bg-background z-10 shadow-sm border-b">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="flex shrink-0 items-center gap-2 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors px-4 py-2"
            >
              <ArrowLeft className="size-5" />
              <span className="font-semibold">Volver</span>
            </button>

            <div className="flex items-center gap-4 flex-1">
              {product.imageurl ? (
                <div className="relative size-16 shrink-0 rounded-xl overflow-hidden shadow-sm">
                  <Image alt={name} className="object-cover" fill sizes="100px" src={product.imageurl} />
                </div>
              ) : (
                <div className="size-16 shrink-0 rounded-xl bg-muted" />
              )}

              <div className="flex flex-col flex-1">
                <h2 className="text-xl font-bold leading-tight text-balance">{name}</h2>
                <p className="text-2xl font-black text-amber-500">
                  {Number(product.price) === 0 && priceFrom > 0
                    ? `Desde ${formatTotemPrice(priceFrom)}`
                    : formatTotemPrice(Number(product.price))}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-muted/10">
            <div className="flex flex-col gap-10">
              {(product.product_subselection ?? []).map(ps => {
                const sub = ps.subselection;
                if (!sub) return null;
                const items = (sub.subselection_item ?? []).filter(i => i.visible);

                groupCounter++;

                return (
                  <SubselectionGroup
                    key={ps.subselection_id}
                    groupIndex={groupCounter}
                    items={items}
                    maxSelection={sub.max_selection}
                    minSelection={sub.min_selection}
                    name={sub.name}
                    selected={selections[ps.subselection_id] ?? {}}
                    single={sub.single}
                    onChange={(itemId, qty) => handleSubselectionChange(ps.subselection_id, itemId, qty)}
                  />
                );
              })}
            </div>
          </div>

          <div className="shrink-0 border-t p-6 md:p-8 bg-background flex justify-end items-center">
            <Button
              className="h-16 rounded-full text-lg font-bold px-8 shadow-md transition-all active:scale-[0.98] flex items-center gap-3 text-emerald-600 bg-white hover:bg-emerald-50 border-2 border-emerald-500/20"
              disabled={!isValid}
              type="button"
              onClick={handleAdd}
            >
              {editingCartItemId ? 'Guardar cambios' : 'Continuar'}
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                <ArrowRight className="size-5" />
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
